import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import axios, { CancelToken } from 'axios';

import { useAuth } from 'hooks/use-auth';
import {
  Status, parseStatuses,
  StatusLog, parseStatusLogs, applyStatusProps
} from 'models';
import { Time } from 'utils';

ChartJS.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);


interface Props {
  companyId: number;
  factoryId: number;
  equipmentId: string;
  periodDays?: number;
  interval?: number;
  minDurationMsec?: number;
  maxNumOfData?: number;
}

export function EquipmentTimelineChart({
  companyId,
  factoryId,
  equipmentId,
  periodDays = 7,
  interval = 60,
  minDurationMsec = 10000,
  maxNumOfData = 10000
}: Props) {
  const auth = useAuth();

  const [intervalMsec, setIntervalMsec] = useState(Time.secToMsec(interval));
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [statusLogs, setStatusLogs] = useState<StatusLog[] | null>(null);
  const [lastDataTimestamp, setLastDataTimestamp] = useState<Date>();
  const [data, setData] = useState<any>({labels: [], datasets: []});

  const refChart = useRef<ChartJS<any>>(null);
  const refStatusLogs = useRef(statusLogs);
  const refLastDataTimestamp = useRef(lastDataTimestamp);

  function dateFrom(): Date {
    const now = new Date();
    now.setDate(now.getDate() - periodDays + 1);
    now.setHours(0, 0, 0, 0);
    return now;
  }

  function dateTo(): Date {
    return new Date();
  }

  async function fetchStatusLogs(cancelToken: CancelToken) {
    axios.get(`/v0/equipments/${equipmentId}/status-logs`, {
      cancelToken: cancelToken,
      headers: auth.authorizationHeader,
      params: {
        company_id: companyId,
        factory_id: factoryId,
        equipment_id: equipmentId,
        time_from: dateFrom(),
        updated_after: refLastDataTimestamp.current || dateFrom(),
        min_duration: minDurationMsec * 1000
      }
    }).then(res => {
      const _dateFrom = dateFrom();
      let slogs: StatusLog[] = refStatusLogs.current || [];
      let new_slogs = parseStatusLogs(res.data)
      new_slogs = new_slogs.map(slog => applyStatusProps(slog, statuses));
      slogs = slogs.concat(new_slogs);
      slogs.sort((a, b) => a.timeFrom.getTime() - b.timeFrom.getTime());
      refStatusLogs.current = slogs.filter((val, idx, self) => (
        self.findIndex(item => item.timeFrom.getTime() === val.timeFrom.getTime()) === idx
      )).filter(item => (
        item.timeTo.getTime() >= _dateFrom.getTime()
      ));
      setStatusLogs(refStatusLogs.current);
      refLastDataTimestamp.current = slogs.reduce((cur: Date, item) => (
        item.updatedAt && (item.updatedAt > cur ? item.updatedAt : cur) || cur
      ), refLastDataTimestamp.current || _dateFrom);
      setLastDataTimestamp(refLastDataTimestamp.current);
    }).catch(err => {
      if (err.code != 'ERR_CANCELED') {
        console.error(err);
      }
    });
  }

  useEffect(() => {
    const cancelTokenSrc = axios.CancelToken.source();
    fetchStatusLogs(cancelTokenSrc.token);
    const intervalId = setInterval(fetchStatusLogs , intervalMsec, cancelTokenSrc.token);
    return () => {
      cancelTokenSrc.cancel(`Cancel API request ID: ${intervalId}`);
      clearInterval(intervalId);
    };
  }, [companyId, factoryId, equipmentId, periodDays, intervalMsec]);

  useEffect(() => {
    setStatusLogs(statusLogs?.map(slog => applyStatusProps(slog, statuses)) || []);
  }, [statuses]);

  useEffect(() => {
    axios.get(`/v0/factories/${factoryId}/statuses`, {
      headers: auth.authorizationHeader,
      params: {
        equipment_id: equipmentId
      }
    }).then(res => {
      setStatuses(parseStatuses(res.data));
    }).catch(err => {
      console.error(err);
    });
  }, [companyId, factoryId, equipmentId, lastDataTimestamp]);

  useEffect(() => {
    setData(makeData(
      statuses,
      statusLogs || [],
      dateFrom(),
      dateTo(),
      minDurationMsec,
      maxNumOfData
    ));
    refChart.current?.update('none');
  }, [statusLogs]);

  return (
    <div>
      <Bar
        ref={refChart}
        options={{
          responsive: true,
          animation: false,
          normalized: true,
          indexAxis: 'y' as const,
          plugins: {
            title: {
              display: true,
              text: 'タイムライン',
            },
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                title: (items: TooltipItem<any>[]) => {
                  return items[0].label;
                },
                label: (item: TooltipItem<any>) => {
                  return item.dataset.label;
                },
                afterLabel: (item: TooltipItem<any>) => {
                  const times = item.raw as number[];
                  const from = Time.dateFromUnixMsec(times[0]);
                  const to = Time.dateFromUnixMsec(times[1]);
                  return `${from.toTimeString()} => ${to.toTimeString()}`;
                },
              }
            }
          },
          scales: {
            y: {
              stacked: true,
            },
            x: {
              type: 'time',
              min: Time.timestampFromHours(0, 0, 0, 0).getTime(),
              max: Time.timestampFromHours(23, 59, 59, 999).getTime(),
            }
          }
        }}
        data={data}
        redraw={false}
      />
      {(statusLogs && statusLogs.length < 1) ? (
        `No data`
      ) : (
        <>
        {lastDataTimestamp ? (
          `最終データ日時: ${lastDataTimestamp.toLocaleString()}`
        ) : (
          `Loading ...`
        )}
        </>
      )}
    </div>
  );
}

function makeData(
  statuses: Status[],
  statusLogs: StatusLog[],
  dataTimeFrom: Date,
  dataTimeTo: Date,
  minDurationMsec: number,
  maxNumOfData: number
): any {
  const dayMsec = 24 * 60 * 60 * 1000;
  const dateDiffDays = Math.ceil((dataTimeTo.getTime() - dataTimeFrom.getTime()) / dayMsec);

  let labels: string[] = [];
  let datasets: any[] = [];

  let filteredStatusLogs = statusLogs.filter(slog => (
    (slog.timeTo.getTime() - slog.timeFrom.getTime()) > minDurationMsec
  ));

  if (filteredStatusLogs.length > maxNumOfData) {
    filteredStatusLogs.sort((a, b) => (
      (b.timeTo.getTime() - b.timeFrom.getTime()) - (a.timeTo.getTime() - a.timeFrom.getTime())
    ));
    filteredStatusLogs = filteredStatusLogs.slice(0, maxNumOfData);
  }

  for (let day = 0; day < dateDiffDays; ++day) {
    const dateBeginMsec = dataTimeFrom.getTime() + (dateDiffDays - day - 1) * dayMsec;
    const dateEndMsec = dateBeginMsec + dayMsec - 1;
    const dateBegin = Time.dateFromUnixMsec(dateBeginMsec);
    const dateEnd = Time.dateFromUnixMsec(dateEndMsec);
    const dayStatusLogs: StatusLog[] = [];
    filteredStatusLogs.filter(slog => (
      !(dateEndMsec <= slog.timeFrom.getTime() || dateBeginMsec >= slog.timeTo.getTime())
    )).map(slog => {
      slog.timeFrom = Time.max(slog.timeFrom, dateBegin);
      slog.timeTo = Time.min(slog.timeTo, dateEnd);
      return slog;
    }).forEach(slog => {
      const lastLog = dayStatusLogs[dayStatusLogs.length - 1];
      if (lastLog && lastLog.statusId === slog.statusId && lastLog.timeTo >= slog.timeFrom) {
        lastLog.timeTo = Time.max(lastLog.timeTo, slog.timeTo);
      } else {
        dayStatusLogs.push(slog);
      }
    });

    labels.push(dateBegin.toDateString());
    datasets = datasets.concat(dayStatusLogs.map(slog => {
      const data = new Array(day);
      data.fill(null);
      return {
        label: slog.statusName || 'NoStatus',
        data: data.concat([[Time.timestamp(slog.timeFrom), Time.timestamp(slog.timeTo)]]),
        borderColor: slog.statusColorRgb?.toString() || '#000',
        backgroundColor: slog.statusColorRgb?.toString() || '#000',
        barPercentage: 1
      };
    }));
  }

  return {
    labels: labels,
    datasets: datasets
  };
}
